import React, { useState, useEffect, useRef } from "react";
import styles from "./CygnusGptDashboard.module.css";
import { Button, Row, Col, Form, Image } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { capitalizeFirstLetter } from "./CygnusGptDashboard.utils";
import { v4 as uuidv4 } from "uuid";
import { Comment } from "react-loader-spinner";
import { makeCygnusApiCall } from "../../../api/api";
import { RichText } from "./components/RichText/RichText";

type ChatAuthor = "user" | "chatgpt" | "assistant";

interface ChatMessage {
  ChatBy: ChatAuthor;
  Chat: string;
  IsFavourite?: boolean;
  SimilarQuestions?: string | string[];
}

interface ChatHistoryPayload {
  ChatMessage: ChatMessage[];
  RecommendedQuestions: string[];
  FavouriteQuestions?: { Question: string }[];
  Explanation: any;
}

interface ChatGptGetResponse {
  success: boolean;
  message?: string;
  data: ChatHistoryPayload;
}

interface ChatPostResponse {
  success: boolean;
  message?: string;
  data: ChatHistoryPayload;
}

interface CygnusGptDashboardProps {
  [key: string]: unknown;
}

// Normalize SimilarQuestions which may be a single string
const normalizeSimilar = (sim?: string | string[]): string[] => {
  if (!sim) return [];
  if (Array.isArray(sim)) return sim.filter(Boolean);
  return sim.trim() ? [sim.trim()] : [];
};

const CygnusGptDashboard: React.FC<CygnusGptDashboardProps> = () => {
  const username =
    typeof window !== "undefined" ? localStorage.getItem("user") : "";

  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [sampleQuestions, setSampleQuestions] = useState<string[]>([]);
  const [recommendedNow, setRecommendedNow] = useState<string[]>([]);
  const [value, setValue] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [theme, setTheme] = useState<"light" | "dark">("light");

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(scrollToBottom, [chat]);

  const onInput = (e: React.ChangeEvent<HTMLInputElement>) =>
    setValue(e.target.value);

  const ask = async (prompt: string) => {
    const userMsg: ChatMessage = { ChatBy: "user", Chat: prompt };
    const loading: ChatMessage = { ChatBy: "assistant", Chat: "loading" };

    setIsLoading(true);
    setChat((prev) => [...prev, userMsg, loading]);

    const resp = (await makeCygnusApiCall({
      type: "POST",
      payload: { prompt },
      url: "/aiagent/",
      show_message: false,
      customized_success_message: "Successful.",
      customized_failure_message:
        "Unable to respond to the query, please try again. If the problem persists, please contact support and attach a screenshot.",
      customized_error_message:
        "Error occured while responding to this query, please contact support and attach a screenshot.",
      isblob: false,
    })) as ChatPostResponse;

    if (resp?.success) {
      // after a POST
      const latest = resp.data?.ChatMessage?.[0];
      const explanation = resp.data?.Explanation; // string[] | undefined

      const assistantMsg: ChatMessage = latest ?? {
        ChatBy: "assistant",
        Chat: "Sorry, I got an empty reply.",
      };

      (assistantMsg as any).__explanation = explanation ?? [];

      setChat((prev) => {
        const next = [...prev];
        next.pop(); // remove loading
        next.push(assistantMsg);
        return next;
      });

      const similar = normalizeSimilar(latest?.SimilarQuestions);
      const globalRecs = resp.data?.RecommendedQuestions ?? [];
      setRecommendedNow(similar.length ? similar : globalRecs);
      setSampleQuestions(globalRecs);
    }

    setIsLoading(false);
  };

  const addChat = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;
    await ask(value.trim());
    setValue("");
  };

  const addTileChat = async (prompt: string) => {
    if (!prompt?.trim()) return;
    await ask(prompt.trim());
  };

  useEffect(() => {
    (async () => {
      const data = (await makeCygnusApiCall({
        type: "GET",
        payload: {},
        url: "/aiagent/",
        show_message: false,
        customized_success_message: "Successful.",
        customized_failure_message:
          "Unable to fetch chat, please try again. If the problem persists, please contact support and attach a screenshot.",
        customized_error_message:
          "Error occured while fetching chat, please contact support and attach a screenshot.",
        isblob: false,
      })) as ChatGptGetResponse;

      if (data?.success && data.data) {
        setChat(data.data.ChatMessage ?? []);
        setSampleQuestions(data.data.RecommendedQuestions ?? []);
        setRecommendedNow([]);
      }
    })();
  }, []);

  const clearChat = async () => {
    const resp = (await makeCygnusApiCall({
      type: "POST",
      payload: { prompt: "__clear__" },
      url: "/aiagent/",
      show_message: false,
      customized_success_message: "Successful.",
      customized_failure_message:
        "Unable to clear chat, please try again. If the problem persists, please contact support and attach a screenshot.",
      customized_error_message:
        "Error occured while clearing chat, please contact support and attach a screenshot.",
      isblob: false,
    })) as ChatGptGetResponse;

    if (resp?.success) {
      setChat(resp.data?.ChatMessage ?? []);
      setRecommendedNow([]);
    }
  };

  const UserBubble: React.FC<{ text: string }> = ({ text }) => (
    <div className={`${styles.bubbleRow} ${styles.userRow}`}>
      <div className={styles.avatarWrap}>
        <div className={styles.userAvatar}>
          <Icon.PersonFill />
        </div>
      </div>
      <div className={`${styles.bubble} ${styles.userBubble}`}>{text}</div>
    </div>
  );

  const BotBubble: React.FC<{ text: string; explanation?: string[] }> = ({
    text,
    explanation,
  }) => (
    <div className={`${styles.bubbleRow} ${styles.botRow}`}>
      <div className={styles.avatarWrap}>
        <div className={styles.botAvatar}>
          <Image
            src={require("../../../images/undefined.png")}
            onError={(e: any) => (e.currentTarget.style.display = "none")}
            alt="Cygnus"
            className={styles.botLogo}
          />
        </div>
    </div>
        {text === "loading" ? (
          <div className={`${styles.bubble} ${styles.botBubble}`}>
            <Comment
              visible
              height="48"
              width="48"
              ariaLabel="comment-loading"
              backgroundColor="transparent"
            />
          </div>
        ) : (
          <div className={`${styles.bubble} ${styles.botBubble}`}>
            <RichText htmlOrText={text} />
            {/* {text} */}
        {explanation && explanation.length > 0 && (
          <details className={styles.explainBlock}>
            <summary className={styles.explainSummary}>Why this answer</summary>
            <ul className={styles.explainList}>
              {explanation.map((e, i) => (
                <li key={i}>
                  <RichText htmlOrText={e} />
                </li>
              ))}
            </ul>
          </details>
        )}
        </div>
        )}
      </div>
  );

  const ChatFeed: React.FC<{ messages: ChatMessage[] }> = ({ messages }) => {
    if (!messages?.length) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyCard}>
            <div className={styles.brandMark} />
            <h4>Cygnus conversational insights</h4>
            <p>
              Ask about clients, engagements, statuses, or anything that makes
              your dashboards sweat.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className={styles.feedScroll}>
        <div className={styles.feedInner}>
          {messages.map((m) =>
            m.ChatBy !== "user" ? (
              <BotBubble
                key={uuidv4()}
                text={m.Chat}
                explanation={(m as any).__explanation}
              />
            ) : (
              <UserBubble key={uuidv4()} text={m.Chat} />
            )
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
    );
  };

  const Chips: React.FC<{
    title?: string;
    items: string[];
    variant?: "solid" | "outline";
  }> = ({ title, items, variant = "solid" }) => {
    if (!items?.length) return null;
    return (
      <div className={styles.chipsBlock}>
        {title && <div className={styles.chipsTitle}>{title}</div>}
        <div className={styles.chipsWrap}>
          {items.map((q, i) => (
            <button
              key={`${q}-${i}`}
              type="button"
              className={`${styles.chip} ${
                variant === "outline" ? styles.chipOutline : ""
              }`}
              onClick={() => addTileChat(q)}
              title={q}
            >
              <Icon.SendFill className={styles.chipIcon} />
              <span className={styles.chipText}>
                <RichText htmlOrText={q} />
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`${styles.appShell} ${
        theme === "dark" ? styles.themeDark : styles.themeLight
      }`}
    >
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.logoDot} />
          <div className={styles.headerTitle}>
            <span className={styles.titleTop}>Cygnus</span>
            <span className={styles.titleBottom}>conversational insights</span>
          </div>
        </div>
        <div className={styles.headerRight} style={{ marginLeft: -100 }}>
          <Button
            className={styles.ghostBtn}
            onClick={() => setTheme(theme === "light" ? "dark" : "light")}
          >
            {theme === "light" ? <Icon.Moon /> : <Icon.Sun />}
          </Button>
          <Button className={styles.primaryBtn} onClick={clearChat}>
            <Icon.Trash3 className={styles.btnIcon} /> Clear chat
          </Button>
        </div>
        <div></div>
      </header>

      {/* Content */}
      <main className={styles.main}>
        <div className={styles.centerCol}>
          <ChatFeed messages={chat} />
        </div>
      </main>

      {/* Composer */}
      <div className={styles.composerWrap}>
        <div className={styles.centerCol}>
          <div className={styles.composerCard}>
            <Chips title="Recommended" items={recommendedNow} />
            <Form onSubmit={addChat}>
              <div className={styles.inputRow}>
                <Form.Control
                  type="text"
                  onChange={onInput}
                  value={value}
                  className={theme==='dark' ? styles.inputFieldDark : styles.inputFieldLight}
                  placeholder="Ask a question…"
                />
                <button
                  className={styles.sendBtn}
                  type="submit"
                  disabled={isLoading || !value.trim()}
                >
                  <Icon.Send />
                </button>
              </div>
            </Form>
            {/* <Chips title="Suggested" items={sampleQuestions} variant="outline" /> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CygnusGptDashboard;
